import isEqual from 'lodash/isEqual';

import type { PrimaryEntity } from '../PageConfigProvider/types/chart';
import type { MetricValues } from '../PageConfigProvider/types/metric';
import type { PageState, SubfilterTree, SubsidiaryDrill } from './types';

type SetPageStateAction = {
  type: 'SET_PAGE_STATE';
  payload: PageState;
};

type SetSelectionsAction = {
  type: 'SET_SELECTIONS';
  payload: {
    plotId: string;
    selections: SubfilterTree[];
    updateInitial?: boolean;
    fromChartSwitcher?: boolean;
  };
};

type SetPrimaryEntityAction = {
  type: 'SET_PRIMARY_ENTITY';
  payload: {
    plotId: string;
    primaryEntity: PrimaryEntity;
    fromChartSwitcher?: boolean;
    entityKey?: string;
  };
};

type SetMultiPrimaryEntitiesAction = {
  type: 'SET_MULTI_PRIMARY_ENTITIES';
  payload: {
    plotId: string;
    primaryEntities: PrimaryEntity[];
    fromChartSwitcher?: boolean;
  };
};

type ClearChartSwitcherPlotAction = {
  type: 'CLEAR_CHART_SWITCHER_PLOT';
};

type ClearPrimaryEntitiesAction = {
  type: 'CLEAR_PRIMARY_ENTITIES';
  payload: {
    plotIds: string[];
  };
};

type ClearMultiPrimaryEntitiesAction = {
  type: 'CLEAR_MULTI_PRIMARY_ENTITIES';
  payload: {
    plotIds: string[];
  };
};

type SetActiveTabAction = {
  type: 'SET_ACTIVE_TAB';
  payload: string;
};

type SetMetricOverrideAction = {
  type: 'SET_METRIC_OVERRIDE';
  payload: {
    plotId: string;
    metric: MetricValues;
    fromChartSwitcher?: boolean;
  };
};

type SetSubsidiaryDrillAction = {
  type: 'SET_SUBSIDIARY_DRILL';
  payload: SubsidiaryDrill;
};

type ShareOfSegmentAction = SetSelectionsAction;

export type PageStateAction =
  | SetPageStateAction
  | ShareOfSegmentAction
  | SetActiveTabAction
  | SetMetricOverrideAction
  | SetPrimaryEntityAction
  | SetMultiPrimaryEntitiesAction
  | ClearPrimaryEntitiesAction
  | ClearMultiPrimaryEntitiesAction
  | ClearChartSwitcherPlotAction
  | SetSubsidiaryDrillAction;

const chartSwitcherPlotPatch = (plotId: string, fromChartSwitcher?: boolean) =>
  fromChartSwitcher ? { chartSwitcherPlotId: plotId } : {};

const chartSwitcherPlotUnchanged = (
  state: PageState,
  plotId: string,
  fromChartSwitcher?: boolean
) => !fromChartSwitcher || state.chartSwitcherPlotId === plotId;

export const pageStateReducer = (
  state: PageState,
  action: PageStateAction
): PageState => {
  switch (action.type) {
    case 'SET_PAGE_STATE': {
      return action.payload;
    }

    case 'SET_ACTIVE_TAB': {
      if (state.activeTabId === action.payload) {
        return state;
      }
      return {
        ...state,
        activeTabId: action.payload,
      };
    }
    case 'SET_SELECTIONS': {
      const nextInitialSelections = action.payload.updateInitial
        ? action.payload.selections
        : (state.initialSelections[action.payload.plotId] ??
          action.payload.selections);
      if (
        chartSwitcherPlotUnchanged(
          state,
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ) &&
        isEqual(
          state.selections[action.payload.plotId],
          action.payload.selections
        ) &&
        isEqual(
          state.initialSelections[action.payload.plotId],
          nextInitialSelections
        )
      ) {
        return state;
      }
      return {
        ...state,
        selections: {
          ...state.selections,
          [action.payload.plotId]: action.payload.selections,
        },
        initialSelections: {
          ...state.initialSelections,
          [action.payload.plotId]: nextInitialSelections,
        },
        ...chartSwitcherPlotPatch(
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ),
      };
    }
    case 'SET_PRIMARY_ENTITY': {
      const key = action.payload.entityKey ?? action.payload.plotId;
      if (
        chartSwitcherPlotUnchanged(
          state,
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ) &&
        isEqual(state.primaryEntities[key], action.payload.primaryEntity)
      ) {
        return state;
      }
      return {
        ...state,
        primaryEntities: {
          ...state.primaryEntities,
          [key]: action.payload.primaryEntity,
        },
        ...chartSwitcherPlotPatch(
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ),
      };
    }
    case 'SET_MULTI_PRIMARY_ENTITIES': {
      if (
        chartSwitcherPlotUnchanged(
          state,
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ) &&
        isEqual(
          state.multiPrimaryEntities[action.payload.plotId],
          action.payload.primaryEntities
        )
      ) {
        return state;
      }
      return {
        ...state,
        multiPrimaryEntities: {
          ...state.multiPrimaryEntities,
          [action.payload.plotId]: action.payload.primaryEntities,
        },
        ...chartSwitcherPlotPatch(
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ),
      };
    }
    case 'CLEAR_CHART_SWITCHER_PLOT': {
      if (state.chartSwitcherPlotId === null) {
        return state;
      }
      return {
        ...state,
        chartSwitcherPlotId: null,
      };
    }
    case 'CLEAR_PRIMARY_ENTITIES': {
      if (
        !action.payload.plotIds.some(
          (plotId) => plotId in state.primaryEntities
        )
      ) {
        return state;
      }
      return {
        ...state,
        primaryEntities: action.payload.plotIds.reduce((acc, plotId) => {
          const { [plotId]: _, ...remainingPrimaryEntities } = acc;
          return remainingPrimaryEntities;
        }, state.primaryEntities),
      };
    }
    case 'CLEAR_MULTI_PRIMARY_ENTITIES': {
      if (
        !action.payload.plotIds.some(
          (plotId) => plotId in state.multiPrimaryEntities
        )
      ) {
        return state;
      }
      return {
        ...state,
        multiPrimaryEntities: action.payload.plotIds.reduce((acc, plotId) => {
          const { [plotId]: _, ...remainingMultiPrimaryEntities } = acc;
          return remainingMultiPrimaryEntities;
        }, state.multiPrimaryEntities),
      };
    }
    case 'SET_METRIC_OVERRIDE': {
      if (
        chartSwitcherPlotUnchanged(
          state,
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ) &&
        state.metricOverrides[action.payload.plotId] === action.payload.metric
      ) {
        return state;
      }
      return {
        ...state,
        metricOverrides: {
          ...state.metricOverrides,
          [action.payload.plotId]: action.payload.metric,
        },
        ...chartSwitcherPlotPatch(
          action.payload.plotId,
          action.payload.fromChartSwitcher
        ),
      };
    }
    case 'SET_SUBSIDIARY_DRILL': {
      if (isEqual(state.subsidiaryDrill, action.payload)) {
        return state;
      }
      return {
        ...state,
        subsidiaryDrill: action.payload,
      };
    }
    default:
      return state;
  }
};
